import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Schedule {
  id: string;
  day_names: string[];
  start_time: string;
  end_time: string;
  active?: boolean;
  specific_date?: string | null;
  is_holiday?: boolean;
  closed_full_day?: boolean;
  created_at?: string;
  updated_at?: string;
  deletedAt?: string | null;
  [key: string]: any;
}

export interface StoreDevice {
  id: string;
  name: string;
  identifier: string;
}

// The merchant that owns this store — a large, mostly-account-level object
// the store GET response embeds in full; typed loosely beyond the fields
// this app actually reads.
export interface StoreMerchant {
  role: string;
  id: string;
  defaultMenuStoreId?: string | null;
  name: string;
  username: string;
  email?: string;
  phone?: string;
  publicPhone?: string;
  logo?: string;
  loyaltyEnabled?: boolean;
  deletedAt?: string | null;
  [key: string]: any;
}

export interface Store {
  id: string;
  name: string;
  address?: string;
  streetName?: string;
  streetNumber?: string;
  apartment?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phoneNumber?: string;
  // The API returns these as decimal strings (e.g. "40.75567940"), not numbers.
  latitude?: string;
  longitude?: string;
  operationalStatus?: string;
  statusMessage?: string | null;
  lastStatusUpdate?: string | null;
  // Minutes, returned by the API as a numeric string (e.g. "15").
  estimatedPreparationTime?: string;
  // When true, paid orders skip manual KDS accept and go straight to
  // "preparing" (prep timer starts automatically). Defaults to false.
  autoAcceptOrders?: boolean;
  // URL of the store's image, or null when it has none.
  imageUrl?: string | null;
  // Master on/off switch for this store's tax, and the fractional rate
  // (0.08875 = 8.875%) applied when it's on. Replaces the old merchant-wide
  // flat tax rate — tax is now configured per store.
  taxable?: boolean;
  taxRate?: number;
  merchant?: StoreMerchant;
  devices?: StoreDevice[];
  schedules?: Schedule[];
  [key: string]: any;
}

export interface CreateStorePayload {
  name: string;
  address?: string;
  city?: string;
  phoneNumber?: string;
  streetName?: string;
  streetNumber?: string;
  apartment?: string;
  state?: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  // Set to null to remove an existing image on update. To upload a new
  // image, pass `imageFile` instead — the mutation switches to a multipart
  // request and this field is ignored in that case.
  imageUrl?: string | null;
  // Minutes. Sent as a number on create/update; the API returns it back as a
  // numeric string on GET (see the `estimatedPreparationTime` field on Store).
  estimatedPreparationTime?: number;
  // When true, paid orders skip manual KDS accept and go straight to
  // "preparing" (prep timer starts automatically). Defaults to false.
  autoAcceptOrders?: boolean;
  // Master on/off switch for this store's tax. Turning it off does NOT
  // clear taxRate server-side — the rate is preserved for re-enabling later.
  taxable?: boolean;
  // Fraction in [0, 1], e.g. 0.08875 for 8.875%. Required to be > 0 when
  // taxable is true.
  taxRate?: number;
  // Required when the creating user's role is "admin" — the backend has no
  // other way to associate the store with a merchant for that role. Ignored
  // by the backend (merchant is derived from the JWT instead) for merchant/
  // merchant_manager roles.
  merchantId?: string;
}

export interface UpdateStorePayload extends CreateStorePayload {
  id: string;
}

interface StoresState {
  activeStoreId: string | null;
}

const initialState: StoresState = {
  activeStoreId: localStorage.getItem('activeStoreId') || null,
};

const storesSlice = createSlice({
  name: 'stores',
  initialState,
  reducers: {
    setActiveStore: (state, action: PayloadAction<string | null>) => {
      state.activeStoreId = action.payload;
      if (action.payload) {
        localStorage.setItem('activeStoreId', action.payload);
      } else {
        localStorage.removeItem('activeStoreId');
      }
    },
  },
});

export const { setActiveStore } = storesSlice.actions;
export default storesSlice.reducer;
